import React, { useState } from 'react';
import { Heart, Sparkles, ChevronLeft, ChevronRight, X } from 'lucide-react';

const publicPath = (p) => (process.env.PUBLIC_URL || '') + p;

const accents = ['#E64B2B', '#F4B93E', '#4FB3B8', '#7BB7DC', '#2C3E50'];
const shadowVariants = ['paper-shadow-red', 'paper-shadow-yellow', 'paper-shadow-teal', 'paper-shadow-sm'];

const pikachuians = [
  { src: 's/student1.jpeg', name: 'Aarohi', stage: 'Class 8 • Maths' },
  { src: 's/student2.jpeg', name: 'Vanya', stage: 'Class 5 • Science' },
  { src: 's/student3.jpeg', name: 'Anaya', stage: 'Class 9 • CBSE' },
  { src: 's/student4.jpeg', name: 'Kavya', stage: 'Class 6 • All Subjects' },
  { src: 's/student5.jpeg', name: 'Myra', stage: 'Class 4 • Maths & English' },
  { src: 's/student6.jpeg', name: 'Dhruv', stage: 'Class 7 • Science' },
  { src: 's/student7.jpeg', name: 'Ira', stage: 'Class 3 • English' },
  { src: 's/student8.jpeg', name: 'Reyansh', stage: 'Class 5 • Maths' },
  { src: 's/student9.jpeg', name: 'Aadhya', stage: 'Class 4 • CBSE' },
  { src: 's/student10.jpeg', name: 'Arnav', stage: 'Class 6 • Science & Maths' },
  { src: 's/student11.jpeg', name: 'Zara', stage: 'Class 6 • All Subjects' },
  { src: 's/student12.jpeg', name: 'Kabeer', stage: 'Class 5 • Maths' },
  { src: 's/student13.jpeg', name: 'Amay', stage: 'Class 3 • English & EVS' },
  { src: 's/student14.jpeg', name: 'Yuvaan & Aayush', stage: 'Class 4 & Class 1' },
  { src: 's/student15.jpeg', name: 'Aarushi', stage: 'Class 9 • Science' },
].map((p, i) => ({
  ...p,
  src: publicPath('/assets/students' + p.src.slice(1)),
  accent: accents[i % accents.length],
}));

export default function Pikachuians() {
  const [lightbox, setLightbox] = useState(null);

  const close = () => setLightbox(null);
  const prev = () => setLightbox((i) => (i - 1 + pikachuians.length) % pikachuians.length);
  const next = () => setLightbox((i) => (i + 1) % pikachuians.length);

  return (
    <section id="pikachuians" className="py-16 lg:py-24 bg-[#FFF8EC] relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#F4B93E]/25 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#4FB3B8]/25 blur-3xl" />

      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="inline-flex items-center gap-2 bg-white text-[#2C3E50] px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest border-2 border-[#2C3E50]">
            <Sparkles size={12} className="text-[#E64B2B]" /> The Pikachuian family
          </span>
          <h2 className="mt-4 text-3xl lg:text-5xl font-black text-[#2C3E50] leading-[1.05]">
            Meet our <span className="text-[#E64B2B]">Pikachuians</span>
            <Heart className="inline-block mx-2 text-[#E64B2B] fill-[#E64B2B]" size={38} />
          </h2>
          <p className="mt-4 text-[#2C3E50]/75 text-lg">
            Real students. Real classrooms. Real smiles. Every child gets a personal tutor and a printed &ldquo;I am Pikachuian&rdquo; welcome badge.
          </p>
        </div>

        {/* Masonry-ish gallery */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {pikachuians.map((p, i) => (
            <button
              type="button"
              key={p.src}
              onClick={() => setLightbox(i)}
              className={`group relative bg-white border-2 border-[#2C3E50] rounded-2xl overflow-hidden text-left hover:-translate-y-1 ${shadowVariants[i % shadowVariants.length]}`}
            >
              <div className="relative overflow-hidden">
                <img
                  src={p.src}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-56 object-cover object-top transition-transform duration-500 group-hover:scale-[1.05]"
                />
                <div
                  className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest text-white border-2 border-white"
                  style={{ background: p.accent }}
                >
                  Pikachuian
                </div>
              </div>
              <div className="p-3">
                <div className="text-base font-black text-[#2C3E50] leading-tight truncate" style={{ fontFamily: 'Fraunces, serif' }}>
                  {p.name}
                </div>
                <div className="text-[10px] font-black uppercase tracking-widest text-[#2C3E50]/60 truncate">{p.stage}</div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <p className="text-[#2C3E50] font-bold">
            Ready to have your child join the Pikachuian family?
          </p>
          <a
            href="#register"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[#E64B2B] text-white font-black border-2 border-[#2C3E50] paper-shadow-sm hover:-translate-y-0.5"
          >
            Register for FREE Demo
          </a>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[60] bg-[#2C3E50]/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
        >
          <button
            onClick={(e) => { e.stopPropagation(); close(); }}
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white text-[#2C3E50] border-2 border-[#2C3E50] flex items-center justify-center hover:bg-[#E64B2B] hover:text-white"
            aria-label="Close"
          >
            <X size={20} strokeWidth={3} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white text-[#2C3E50] border-2 border-[#2C3E50] flex items-center justify-center hover:bg-[#F4B93E]"
            aria-label="Previous"
          >
            <ChevronLeft size={22} strokeWidth={3} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white text-[#2C3E50] border-2 border-[#2C3E50] flex items-center justify-center hover:bg-[#F4B93E]"
            aria-label="Next"
          >
            <ChevronRight size={22} strokeWidth={3} />
          </button>
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-white border-2 border-[#2C3E50] rounded-3xl overflow-hidden paper-shadow">
              <img
                src={pikachuians[lightbox].src}
                alt={pikachuians[lightbox].name}
                className="w-full max-h-[70vh] object-contain bg-[#FFF8EC]"
              />
              <div className="p-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-xl font-black text-[#2C3E50]" style={{ fontFamily: 'Fraunces, serif' }}>
                    {pikachuians[lightbox].name}
                  </div>
                  <div className="text-xs font-black uppercase tracking-widest text-[#2C3E50]/60">
                    {pikachuians[lightbox].stage}
                  </div>
                </div>
                <div
                  className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-white border-2 border-white"
                  style={{ background: pikachuians[lightbox].accent }}
                >
                  I am Pikachuian
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
